import React, { useState, useRef, useEffect } from "react";
import { Button, Toggle, Dropdown } from "../../../components/tailwind";
import { Icon } from "../../../components/tailwind/icon/Icon";
import type { Option } from "../../../components/tailwind/dropdown/Dropdown";
import { uuidv4 } from "../../../utils/uuidUtils";

type AdditionalInstruction = {
  id: string;
  value: string;
};

type ListRowProps = {
  instruction: AdditionalInstruction;
  onChange: (id: string, value: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  inputType?: "textarea" | "input";
  placeholder?: string;
};

const ListRow: React.FC<ListRowProps> = ({
  instruction,
  onChange,
  onRemove,
  canRemove,
  inputType = "textarea",
  placeholder = "",
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (inputType === "textarea") {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  }, [instruction.value, inputType]);

  return (
    <div className="tw-flex tw-items-start tw-gap-2 tw-w-full">
      <div className="tw-flex-1 tw-min-w-0">
        {inputType === "input" ? (
          <input
            type="text"
            value={instruction.value}
            onChange={(e): void => {
              onChange(instruction.id, e.target.value);
            }}
            className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
            placeholder={placeholder}
          />
        ) : (
          <textarea
            ref={textareaRef}
            value={instruction.value}
            onChange={(e): void => {
              onChange(instruction.id, e.target.value);
              // Auto-resize on change
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            rows={1}
            className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5 tw-resize-none tw-overflow-hidden"
            style={{ minHeight: "38px", height: "38px" }}
            placeholder={placeholder}
          />
        )}
      </div>
      {canRemove && (
        <Button
          type="button"
          variant="secondary-alt"
          icon
          onClick={(): void => onRemove(instruction.id)}
          borderless
          data-balloon="Remove instruction"
          data-balloon-pos="left"
        >
          <Icon className="tw-text-red-600" name="delete" />
        </Button>
      )}
    </div>
  );
};

type Props = {
  token: string;
  globalDefaultInstruction: string;
  globalDefaultModelDisplayName: string;
  hasCustomInstruction: string;
  instruction: string;
  additionalInstructions: string; // JSON string
  modelName: string;
  useImages: string;
  modelOptions: string; // JSON string
  webIdentificationFields: string; // JSON string
  supplierTextFieldId: string;
  pimDatestampFieldId: string;
  formAction: string;
  csrfToken: string;
};

export const ManageEnrichPrompt: React.FC<Props> = ({
  globalDefaultInstruction,
  globalDefaultModelDisplayName,
  hasCustomInstruction,
  instruction: initialInstruction,
  additionalInstructions: initialAdditionalInstructionsJson,
  modelName: initialModelName,
  useImages: initialUseImages,
  modelOptions: modelOptionsJson,
  webIdentificationFields: initialWebIdentificationFieldsJson,
  supplierTextFieldId: initialSupplierTextFieldId,
  pimDatestampFieldId: initialPimDatestampFieldId,
  formAction,
  csrfToken,
}) => {
  const [instruction, setInstruction] = useState<string>(
    initialInstruction || ""
  );
  const [hasCustomInstructionState, setHasCustomInstructionState] = useState<
    boolean
  >(hasCustomInstruction === "true");
  const [additionalInstructions, setAdditionalInstructions] = useState<
    AdditionalInstruction[]
  >(() => {
    try {
      const parsed = JSON.parse(initialAdditionalInstructionsJson || "[]");
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((value: string) => ({
          id: uuidv4(),
          value: value,
        }));
      }
      return [{ id: uuidv4(), value: "" }];
    } catch {
      return [{ id: uuidv4(), value: "" }];
    }
  });
  const [modelName, setModelName] = useState<string>(initialModelName || "");
  const [useImages, setUseImages] = useState<boolean>(
    initialUseImages === "true"
  );
  const [webIdentificationFields, setWebIdentificationFields] = useState<
    AdditionalInstruction[]
  >(() => {
    try {
      const parsed = JSON.parse(initialWebIdentificationFieldsJson || "[]");
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((value: string) => ({
          id: uuidv4(),
          value: value,
        }));
      }
      return [{ id: uuidv4(), value: "" }];
    } catch {
      return [{ id: uuidv4(), value: "" }];
    }
  });
  const [supplierTextFieldId, setSupplierTextFieldId] = useState<string>(
    initialSupplierTextFieldId || ""
  );
  const [pimDatestampFieldId, setPimDatestampFieldId] = useState<string>(
    initialPimDatestampFieldId || ""
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);

  // Parse model options
  const modelOptions: Option<string>[] = React.useMemo(() => {
    try {
      const parsed = JSON.parse(modelOptionsJson || "[]");
      return Array.isArray(parsed)
        ? parsed.map(
            (opt: { value: string; label: string }, index: number) => ({
              key: `model-${index}`,
              value: opt.value,
              text: opt.label,
            })
          )
        : [];
    } catch {
      return [];
    }
  }, [modelOptionsJson]);

  const handleRevertInstruction = (): void => {
    setInstruction("");
    setHasCustomInstructionState(false);
  };

  const handleAddAdditionalInstruction = (): void => {
    setAdditionalInstructions([
      ...additionalInstructions,
      { id: uuidv4(), value: "" },
    ]);
  };

  const handleRemoveAdditionalInstruction = (id: string): void => {
    if (additionalInstructions.length > 1) {
      const newInstructions = additionalInstructions.filter(
        (inst) => inst.id !== id
      );
      setAdditionalInstructions(newInstructions);
    } else {
      // Keep at least one empty field
      setAdditionalInstructions([{ id: uuidv4(), value: "" }]);
    }
  };

  const handleAdditionalInstructionChange = (
    id: string,
    value: string
  ): void => {
    setAdditionalInstructions(
      additionalInstructions.map((inst) =>
        inst.id === id ? { ...inst, value } : inst
      )
    );
  };

  const handleAddWebIdentificationField = (): void => {
    setWebIdentificationFields([
      ...webIdentificationFields,
      { id: uuidv4(), value: "" },
    ]);
  };

  const handleRemoveWebIdentificationField = (id: string): void => {
    if (webIdentificationFields.length > 1) {
      const newFields = webIdentificationFields.filter(
        (field) => field.id !== id
      );
      setWebIdentificationFields(newFields);
    } else {
      // Keep at least one empty field
      setWebIdentificationFields([{ id: uuidv4(), value: "" }]);
    }
  };

  const handleWebIdentificationFieldChange = (
    id: string,
    value: string
  ): void => {
    setWebIdentificationFields(
      webIdentificationFields.map((field) =>
        field.id === id ? { ...field, value } : field
      )
    );
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert additional instructions to JSON array
      const additionalInstructionsValue = JSON.stringify(
        additionalInstructions
          .map((inst) => inst.value)
          .filter((value) => value.trim() !== "")
      );

      // Convert web identification fields to JSON array
      const webIdentificationFieldsValue = JSON.stringify(
        webIdentificationFields
          .map((field) => field.value)
          .filter((value) => value.trim() !== "")
      );

      // Create form data
      const formData = new FormData();
      formData.append("csrfmiddlewaretoken", csrfToken);

      // Handle instruction:
      // - If hasCustomInstructionState is false, send empty string to set instruction to None (use global default)
      // - If hasCustomInstructionState is true, send the instruction value (even if it matches default)
      if (!hasCustomInstructionState) {
        formData.append("instruction", "");
      } else {
        formData.append("instruction", instruction);
      }

      formData.append("additional_instructions", additionalInstructionsValue);
      formData.append("model_name", modelName);
      formData.append("use_images", useImages ? "on" : "");
      formData.append(
        "web_identification_fields",
        webIdentificationFieldsValue
      );
      formData.append("supplier_text_field_id", supplierTextFieldId);
      formData.append("pim_datestamp_field_id", pimDatestampFieldId);

      const response = await fetch(formAction, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
        redirect: "manual",
      });

      // With redirect: "manual", successful redirects have type "opaqueredirect"
      if (response.ok || response.type === "opaqueredirect") {
        // Reload page to show success message
        window.location.reload();
      } else {
        console.error("Failed to save enrich prompt");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error saving enrich prompt:", error);
      setIsSubmitting(false);
    }
  };

  // Update hasCustomInstructionState when instruction is manually edited
  const handleInstructionChange = (value: string): void => {
    setInstruction(value);
    // Mark as custom if user edits it
    if (!hasCustomInstructionState) {
      setHasCustomInstructionState(true);
    }
  };

  return (
    <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-space-y-6">
      <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-900">
        Enrich Settings
      </h1>

      <form ref={formRef} onSubmit={handleSubmit} className="tw-space-y-6">
        {/* Prompt Settings */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-space-y-6">
          <div className="tw-space-y-2">
            <p className="tw-text-lg tw-font-semibold tw-text-gray-600">
              Prompt Settings
            </p>
            <div className="txu-divider"></div>
            {/* Instruction Field */}
            <div className="tw-flex tw-justify-between">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                  Instruction
                </label>

                <p className="tw-text-xs tw-text-gray-500">
                  Overwrite the global default system instruction
                </p>
              </div>
              {hasCustomInstructionState && (
                <Button
                  type="button"
                  variant="secondary-alt"
                  icon
                  onClick={handleRevertInstruction}
                  borderless
                  data-balloon="Revert to default"
                  data-balloon-pos="left"
                >
                  <Icon name="undo" />
                </Button>
              )}
            </div>

            <div className="tw-relative">
              <textarea
                value={instruction}
                onChange={(e): void => handleInstructionChange(e.target.value)}
                rows={10}
                className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-p-2.5"
                placeholder={globalDefaultInstruction}
              />
            </div>
          </div>
          {/* Additional Instructions Field */}
          <div className="tw-space-y-2">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Additional Instructions
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              Additional instructions to give the AI, useful for adding rules
              without altering the main instruction
            </p>
            <div className="tw-space-y-3">
              {additionalInstructions.map((inst) => (
                <ListRow
                  key={inst.id}
                  instruction={inst}
                  onChange={handleAdditionalInstructionChange}
                  onRemove={handleRemoveAdditionalInstruction}
                  canRemove={additionalInstructions.length > 1}
                  placeholder="Enter additional instruction..."
                />
              ))}
              <Button
                type="button"
                variant="secondary-alt"
                icon
                onClick={handleAddAdditionalInstruction}
                className="tw-flex tw-items-center tw-gap-2"
                size="sm"
                borderless
              >
                <Icon name="add" /> Add
              </Button>
            </div>
          </div>
          {/* Model Name Dropdown */}
          <div className="tw-space-y-2">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Model Name
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              Overwrite the default GPTModel which will be used for enrichment
            </p>
            <Dropdown
              control="search"
              options={modelOptions}
              value={modelName}
              onChange={(value): void => setModelName(value as string)}
              placeholder={globalDefaultModelDisplayName}
              clearable={true}
            />
          </div>
          {/* Use Images Toggle */}
          <div className="tw-space-y-2">
            <Toggle
              checked={useImages}
              setChecked={setUseImages}
              label="Use Images"
            />
            <p className="tw-text-xs tw-text-gray-500">
              Whether images on the document should be used in the enrichment
            </p>
          </div>
        </div>

        {/* PIM Settings */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-space-y-6">
          <div className="tw-space-y-2">
            <p className="tw-text-lg tw-font-semibold tw-text-gray-600">
              PIM Settings
            </p>
            <div className="txu-divider"></div>
            {/* Web Identification Fields */}
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Web Identification Fields
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              Field IDs of PIM fields which help to find information about the
              product online
            </p>
            <div className="tw-space-y-3">
              {webIdentificationFields.map((field) => (
                <ListRow
                  key={field.id}
                  instruction={field}
                  onChange={handleWebIdentificationFieldChange}
                  onRemove={handleRemoveWebIdentificationField}
                  canRemove={webIdentificationFields.length > 1}
                  inputType="input"
                  placeholder="Enter web identification field id"
                />
              ))}
              <Button
                type="button"
                variant="secondary-alt"
                icon
                onClick={handleAddWebIdentificationField}
                className="tw-flex tw-items-center tw-gap-2"
                size="sm"
                borderless
              >
                <Icon name="add" />
              </Button>
            </div>
          </div>

          {/* Supplier Text Field ID */}
          <div className="tw-space-y-2">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              Supplier Text Field ID
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              The ID of the supplier text field in PIM, this will be included in
              the input data in enrichments
            </p>
            <input
              type="text"
              value={supplierTextFieldId}
              onChange={(e): void => setSupplierTextFieldId(e.target.value)}
              className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
              placeholder="Enter supplier text field ID..."
            />
          </div>

          {/* PIM Datestamp Field ID */}
          <div className="tw-space-y-2">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              PIM Datestamp Field ID
            </label>
            <p className="tw-text-xs tw-text-gray-500">
              When an enrichment result is published to PIM, also write
              datestamp to this field
            </p>
            <input
              type="text"
              value={pimDatestampFieldId}
              onChange={(e): void => setPimDatestampFieldId(e.target.value)}
              className="tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-px-2.5 tw-py-1.5"
              placeholder="Enter PIM datestamp field ID..."
            />
          </div>

          {/* Submit Button */}
          <div className="tw-flex tw-justify-end tw-pt-4">
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
